import { useCallback, useEffect, useState } from "react";
import { useInterval } from "@mantine/hooks";
import { create } from "zustand";
import type { Segment } from "../api/types";
import type { PlaybackSnapshot } from "../platform/native";

/**
 * What the player is currently pointed at.
 *
 * Three shapes rather than one, because the three name the opening instant differently: a
 * segment by its own row, an arbitrary instant by itself, and live not at all -- the edge is
 * the server's number and only session creation can ask for it. What follows that instant is
 * the server's business either way; it holds the cursor and walks the filtered queue.
 */
export type PlaybackSource =
  | { kind: "live" }
  | { kind: "dvr"; sourceAtMs: number }
  /**
   * A show, optionally opened part-way through.
   *
   * `atMs` is a wall-clock instant inside the span, and it exists because a segment playlist
   * is the only way to play a show whose audio has left the buffer: there is no window
   * playlist to cut over those hours, so landing a pan inside an archived show has nothing
   * else to ask for. Without it the landing would drag the playhead to the show's first
   * second, which is not where the gesture was aimed.
   */
  | { kind: "segment"; segment: Segment; atMs?: number }
  | { kind: "window"; startMs: number }
  | {
      kind: "presentation";
      presentationId: string | null;
      segmentId: number | null;
      itemPositionUs: number;
      sourceAt: string | null;
      filter?: Record<string, string | number | boolean>;
    };

export type SeekTarget =
  { kind: "source"; atMs: number; segmentId?: number } | { kind: "media"; mediaUs: number };

export interface PlayerState {
  source: PlaybackSource | null;
  /** User intent, independent of loading, buffering, and the media element's observed state. */
  wantsPlayback: boolean;
  playing: boolean;
  /**
   * Wall-clock position of the playhead, in epoch ms.
   *
   * The instant the connection opened at plus `currentTime`, which the transport works out --
   * the body carries no timing of its own and restarts from zero on every reconnect, so the
   * anchor the session reported is the only thing that can place it in wall clock.
   */
  positionMs: number;
  /**
   * When `positionMs` was last reported by an audio element, or null when nothing has reported
   * one since the source changed.
   *
   * The freshness of the position rather than the position itself. At the live edge the clock is
   * the playhead unless live audio is reporting one of its own, and "is reporting" has to be
   * answerable: a live source whose audio never arrives -- an offline stream, a session that
   * fails, a load that stalls, an autoplay refusal -- leaves `positionMs` at the guess `play`
   * wrote below, and deferring to that guess freezes the whole view at the instant play was
   * pressed while the toolbar goes on claiming LIVE.
   */
  positionAt: number | null;
  /**
   * How far behind the clock the live stream last put the playhead, in ms, or zero before one
   * has ever played here.
   *
   * The stream opens behind the published edge and then keeps whatever backlog the server
   * burst into it, so the newest audio this page can actually come to rest on is some seconds
   * older than the edge itself. That is the distance the
   * timeline walls off at the live edge, and it is remembered rather than recomputed: measured
   * only while live audio plays, the wall moved every time the listener switched to an archive
   * show and back, marking a stretch playable in one mode and "not yet" in the other.
   *
   * Kept, not cleared, when the source changes: it is a measurement of this deployment's edge,
   * and it does not stop being true because something else is playing.
   */
  liveLagMs: number;
  /** Set while an audio element is loading, so the transport can show it. */
  loading: boolean;
  error: string | null;
  volume: number;
  muted: boolean;

  /** The block the UI is showing details for. Not necessarily the one playing. */
  selectedId: number | null;

  /** False once the viewer scrubs; the "go live" button puts it back. */
  follow: boolean;

  /**
   * Pinned to the live edge: the wall clock drives the playhead, not an audio element.
   *
   * A refinement of `follow`, not a peer of it. `follow` means the view is slaved to the
   * playhead; `atLive` means the playhead is slaved to the clock, which is only coherent while
   * the view is following it. The setters below keep `atLive` implying `follow`, so "panned
   * away but still live" cannot be represented at all.
   */
  atLive: boolean;

  /** Seek requests are handed to the audio element by the playback hook. */
  seekRequest: { target: SeekTarget; token: number } | null;
  /** Monotonic even after the current request is cleared. */
  seekToken: number;
  /** Monotonic even when the same source is deliberately opened again. */
  sourceToken: number;
  nativeSnapshot: PlaybackSnapshot | null;
  nudgeRequest: { seconds: number; token: number } | null;
  nudgeToken: number;
  stepRequest: { direction: -1 | 1; token: number } | null;
  stepToken: number;

  play: (source: PlaybackSource) => void;
  resume: () => void;
  pause: () => void;
  stop: () => void;
  end: () => void;
  setPlaying: (playing: boolean) => void;
  setPosition: (ms: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  select: (id: number | null) => void;
  setFollow: (follow: boolean) => void;
  setAtLive: (atLive: boolean) => void;
  requestSeek: (target: SeekTarget) => void;
  requestNudge: (seconds: number) => void;
  requestStep: (direction: -1 | 1) => void;
  clearSeek: () => void;
}

/**
 * The most a measured live lag may claim.
 *
 * It walls off the end of the timeline, so a nonsense position -- a skewed clock, a cursor the
 * server opened wrong, a tab resumed after a suspend -- must not be able to wall off the
 * archive with it. Nothing sane is a minute behind: the archive holds back seconds and the
 * stream's own backlog is a few more.
 */
const MAX_LIVE_LAG_MS = 60_000;

const clampLiveLag = (ms: number): number => Math.min(MAX_LIVE_LAG_MS, Math.max(0, ms));

export const usePlayer = create<PlayerState>()((set, get) => ({
  source: null,
  wantsPlayback: false,
  playing: false,
  positionMs: Date.now(),
  // Nothing has played, so nothing has reported a position and the clock is the playhead.
  positionAt: null,
  loading: false,
  error: null,
  volume: 1,
  muted: false,
  selectedId: null,
  follow: true,
  // A page that has just loaded is looking at the live edge, and nothing has been played yet,
  // so the clock is the only thing that can move the playhead.
  atLive: true,
  seekRequest: null,
  seekToken: 0,
  sourceToken: 0,
  nativeSnapshot: null,
  nudgeRequest: null,
  nudgeToken: 0,
  stepRequest: null,
  stepToken: 0,
  // Nothing live has played yet, so the only hold-back known is the server's published one.
  liveLagMs: 0,

  play: (source) =>
    set({
      source,
      sourceToken: get().sourceToken + 1,
      wantsPlayback: true,
      playing: false,
      loading: true,
      error: null,
      // Jump the playhead to the new source immediately rather than waiting for the audio
      // element to report a position: otherwise the timeline slides back to the previous
      // track for the second or so the manifest takes to load.
      positionMs:
        source.kind === "segment"
          ? (source.atMs ?? Date.parse(source.segment.start))
          : source.kind === "window"
            ? source.startMs
            : source.kind === "dvr"
              ? source.sourceAtMs
              : source.kind === "presentation"
                ? source.sourceAt === null
                  ? get().positionMs
                  : Date.parse(source.sourceAt)
                : Date.now(),
      // A guess, not a report: the audio element has not said anything yet.
      positionAt: null,
      selectedId:
        source.kind === "segment"
          ? source.segment.id
          : source.kind === "presentation"
            ? source.segmentId
            : get().selectedId,
      // Which source is playing decides the mode, so every entry point agrees without knowing
      // it has to: the transport's play button on an idle page, the space bar, a row in the
      // stream list, a click on the canvas. Setting this in the callers instead would leave
      // the timeline claiming to be in the archive while live audio played.
      atLive: source.kind === "live",
      follow: source.kind === "live" ? true : get().follow,
      seekRequest: null,
    }),

  resume: () => set({ wantsPlayback: true, error: null }),
  pause: () => set({ wantsPlayback: false }),
  stop: () => set({ source: null, wantsPlayback: false, playing: false, loading: false }),
  end: () => set({ wantsPlayback: false, playing: false, loading: false }),
  setPlaying: (playing) => set({ playing }),
  // Stamped rather than merely stored: `playheadMs` has to be able to tell a position that is
  // still arriving from one that stopped. Live audio also measures the edge on its way past --
  // the report is the only thing that knows how far behind the stream actually settled.
  setPosition: (reportedMs) => {
    // Whole milliseconds. `currentTime` is fractional and every wall-clock reading has to add
    // it to the session's anchor, so the sum lands on a fraction, and the listened report
    // sends this value straight out as an integer epoch.
    // The API refused the whole batch for it, the client put the spans back, and nothing was
    // ever credited: a tracker permanently violet under a show that had just been played.
    const positionMs = Math.round(reportedMs);
    const at = Date.now();
    set(
      get().source?.kind === "live"
        ? { positionMs, positionAt: at, liveLagMs: clampLiveLag(at - positionMs) }
        : { positionMs, positionAt: at },
    );
  },
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  setVolume: (volume) => set({ volume: Math.min(1, Math.max(0, volume)), muted: false }),
  setMuted: (muted) => set({ muted }),
  select: (selectedId) => set({ selectedId }),
  // The pairing is the setters' job, not the callers': letting go of the playhead is exactly
  // what leaving the live edge means, and there is no coherent state where the view has been
  // panned away and the clock is still driving it.
  setFollow: (follow) => set(follow ? { follow } : { follow, atLive: false }),
  setAtLive: (atLive) => set(atLive ? { atLive, follow: true } : { atLive }),
  // A monotonic token, not the timestamp alone: seeking twice to the same instant has to
  // register as two requests, or the second one is silently dropped.
  requestSeek: (target) => {
    const seekToken = get().seekToken + 1;
    set({
      seekToken,
      seekRequest: { target, token: seekToken },
      positionAt: null,
      atLive: false,
      ...(target.kind === "source" ? { positionMs: target.atMs } : {}),
    });
  },
  requestNudge: (seconds) => {
    const nudgeToken = get().nudgeToken + 1;
    set({ nudgeToken, nudgeRequest: { seconds, token: nudgeToken } });
  },
  requestStep: (direction) => {
    const stepToken = get().stepToken + 1;
    set({ stepToken, stepRequest: { direction, token: stepToken } });
  },
  clearSeek: () => set({ seekRequest: null }),
}));

/** The instant a source begins, used to convert playback time to wall clock. */
export function sourceAnchorMs(source: PlaybackSource, fallback: number): number {
  switch (source.kind) {
    case "segment":
      return Date.parse(source.segment.start);
    case "window":
      return source.startMs;
    case "dvr":
      return source.sourceAtMs;
    case "presentation":
      return source.sourceAt === null ? fallback : Date.parse(source.sourceAt);
    case "live":
      return fallback;
  }
}

/**
 * How long a reported position stays the playhead after the audio stops reporting it.
 *
 * `timeupdate` fires several times a second while a stream plays, so anything past a couple of
 * seconds means the audio has stopped speaking rather than that it is between events. Generous
 * enough to sit through a buffering hiccup without the view jumping to the clock and back.
 */
export const POSITION_STALE_MS = 5_000;
const AUDIO_EXTRAPOLATION_MS = 1_000;

/**
 * How far a report may be carried by the clock before the audio has to speak again.
 *
 * `timeupdate` lands a few times a second, and a playhead that only moves when one does is a
 * step function: at the bottom of the zoom range the whole canvas jumps a report at a time
 * instead of sliding. Carrying the last report forward at the rate audio plays -- one
 * millisecond per millisecond -- makes it smooth, and every report re-anchors it, so the error
 * never exceeds one interval of ordinary drift.
 *
 * Capped well short of `POSITION_STALE_MS`: reports stop during a buffering stall, where the
 * audio is standing still and running ahead of it would have to be taken back. A second is
 * several missed reports -- past that the honest thing is to stop.
 */
/**
 * Where the audio has actually got to by `nowMs`, or where it stopped.
 *
 * The playhead's raw material, and separately useful: anything claiming what was *heard* has
 * to measure the audio and never the clock. At the live edge `playheadMs` below hands the
 * playhead to the clock, which goes on running when nothing is playing -- that is right for a
 * cursor pinned to now, and wrong for the grey listened rule, which grew across a whole show
 * while the transport sat paused.
 */
export function audioMs(state: PlayheadInput, nowMs: number): number {
  if (!state.playing || state.positionAt === null) return state.positionMs;
  return state.positionMs + Math.max(0, Math.min(nowMs - state.positionAt, AUDIO_EXTRAPOLATION_MS));
}

/** Just the fields the playhead is a function of. `PlayerState` satisfies it. */
export interface PlayheadInput {
  source: PlaybackSource | null;
  playing: boolean;
  atLive: boolean;
  positionMs: number;
  positionAt: number | null;
}

/**
 * Where the playhead is, in wall-clock terms, at `nowMs`.
 *
 * One rule, in one place, because the canvas and the transport must not disagree about where
 * the playhead is: they used to, and the canvas was the only one that was right. At the live
 * edge the clock is the playhead -- that is what `atLive` means, see the field -- and live audio
 * only overrides it while it is actually reporting a position. That position is a few seconds
 * behind the clock, and honestly so, which is why it wins whenever it exists.
 *
 * It stops winning when it stops arriving. A live stream that has gone quiet while still
 * claiming to play, or that never started at all, would otherwise pin the view to the instant
 * play was pressed: the ruler's start, centre and end all standing still while the clock runs.
 * A live source that is not playing keeps its position rather than being carried on by the
 * clock. Pausing is not how you get there any more -- the pause drops the source (`onPause` in
 * useAudioSession), because a broadcast cannot be resumed where it stopped -- so what is left
 * here is a
 * live source that never started: loaded, silent, and honest about where it last was.
 */
export function playheadMs(state: PlayheadInput, nowMs: number): number {
  // Away from the edge the audio owns the playhead outright, whatever it is playing.
  if (!state.atLive) return audioMs(state, nowMs);
  // Any other source is deferred to only when the view is not at the edge: wheeling to the edge
  // while a show from last Tuesday plays leaves exactly that pair, and the view belongs where it
  // was asked to be rather than back on the audio.
  if (state.source?.kind !== "live") return nowMs;
  if (state.positionAt === null) return nowMs;
  if (!state.playing) return state.positionMs;
  return nowMs - state.positionAt <= POSITION_STALE_MS ? audioMs(state, nowMs) : nowMs;
}

/** How often a clock-driven playhead is re-read for React. */
const PLAYHEAD_TICK_MS = 1000;

/**
 * The playhead, as a value a component can render.
 *
 * The render loop reads the rule above once a frame and re-renders nobody; anything in the DOM
 * needs the clock to arrive as state instead, or it reports the instant the page loaded forever.
 * The tick runs only at the live edge: everywhere else the playhead moves when the audio says so
 * and that already arrives through the store.
 */
export function usePlayheadMs(): number {
  const source = usePlayer((s) => s.source);
  const playing = usePlayer((s) => s.playing);
  const atLive = usePlayer((s) => s.atLive);
  const positionMs = usePlayer((s) => s.positionMs);
  const positionAt = usePlayer((s) => s.positionAt);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // The `useCallback` is load-bearing, not tidiness: `useInterval` re-arms whenever the
  // callback's identity changes, and this hook re-renders several times a second off
  // `positionMs`. An inline arrow would restart the countdown before it ever reached a
  // second, and the clock-driven playhead at the live edge would silently stop moving.
  const tick = useCallback(() => setNowMs(Date.now()), []);
  const interval = useInterval(tick, PLAYHEAD_TICK_MS);
  useEffect(() => {
    if (!atLive) return;
    interval.start();
    return interval.stop;
    // `interval` is a fresh object each render, so listing it -- which is what the rule asks
    // for -- re-arms the countdown several times a second and the live playhead stops moving.
    // The two methods it actually calls are what the effect depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atLive, interval.start, interval.stop]);

  // Not `nowMs` unconditionally: with the tick off it is as old as the last time the view left
  // the edge, and while the rule does not look at it in that state, handing a stale clock to a
  // rule that can change is not a pair worth leaving behind.
  return playheadMs(
    { source, playing, atLive, positionMs, positionAt },
    atLive ? nowMs : Date.now(),
  );
}
